import {
  hasActiveEdit,
  isEditableTarget,
  type SelectionInput,
  type SelectionState,
  type SharedEventLoop,
} from '@databases/selections';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { DatabaseFieldType } from '@databases/types/database';
import { type DatabaseDraft } from '@databases/utils/database/changesets';
import { getDatabaseValue } from '@databases/utils/database/utils';

import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

type CheckboxCellMachine = {
  getState(): SelectionState;
};

export interface CheckboxCellKeyboardInputDeps {
  sharedLoop: SharedEventLoop;
  databaseAdapter: DatabaseAdapter;
  getDatabaseDraft: () => DatabaseDraft;
  setCheckboxValue: (args: { fieldId: string; entryId: string; value: boolean }) => void;
}

function handleCheckboxCellKeyboardDown(
  capture: KeyboardDownCapture,
  machine: CheckboxCellMachine,
  deps: CheckboxCellKeyboardInputDeps,
): void {
  const isToggleKey =
    capture.properties.key === ' ' || capture.properties.key === 'Enter';

  if (!isToggleKey || capture.properties.consumed) {
    return;
  }

  if (isEditableTarget(capture.properties.event.target)) {
    return;
  }

  const state = machine.getState();

  if (state.mode !== 'cell' || !state.focusedCell || hasActiveEdit(state)) {
    return;
  }

  const entryId = deps.databaseAdapter.getVisibleEntryIdAt(state.focusedCell.row.index);
  const fieldId = deps.databaseAdapter.getVisibleFieldIdAt(state.focusedCell.col.index);

  if (!entryId || !fieldId || !deps.databaseAdapter.canEditValue(fieldId, entryId)) {
    return;
  }

  const databaseDraft = deps.getDatabaseDraft();
  const field = databaseDraft.fieldsById[fieldId];
  const entry = databaseDraft.entriesById[entryId];

  if (!field || !entry || field.type !== DatabaseFieldType.CHECKBOX) {
    return;
  }

  const currentValue = getDatabaseValue(databaseDraft, field, entry);
  const isChecked =
    currentValue.type === DatabaseFieldType.CHECKBOX && currentValue.value === true;

  deps.setCheckboxValue({
    fieldId,
    entryId,
    value: !isChecked,
  });

  capture.properties.event.preventDefault();
  capture.properties.event.stopPropagation?.();
  capture.properties.consumed = true;
}

export function createCheckboxCellKeyboardInput(
  machine: CheckboxCellMachine,
  deps: CheckboxCellKeyboardInputDeps,
): SelectionInput {
  let unsubscribe: (() => void) | undefined;

  return {
    setup() {
      unsubscribe = deps.sharedLoop.subscribeSyncKeyboardDown((capture) => {
        handleCheckboxCellKeyboardDown(capture, machine, deps);
      });
    },

    dispose() {
      unsubscribe?.();
    },
  };
}
